import type { LotConfig } from '@/model/schema';
import type { ResolvedConstraints } from './constraints';
import type { StylePresetSpec } from './stylePresets';
import type { Rng } from './rng';

/**
 * Produce a lot whose dimensions sit inside the hard range constraints and,
 * where unconstrained, inside the preset's preferred range.
 */
export function generateLot(
  rng: Rng,
  preset: StylePresetSpec,
  constraints: ResolvedConstraints,
): LotConfig {
  const wMin = Math.max(constraints.lotWidth.min, constraints.lotWidth.hard ? constraints.lotWidth.min : preset.lotWidth.min);
  const wMax = Math.min(constraints.lotWidth.max, constraints.lotWidth.hard ? constraints.lotWidth.max : preset.lotWidth.max);
  const dMin = Math.max(constraints.lotDepth.min, constraints.lotDepth.hard ? constraints.lotDepth.min : preset.lotDepth.min);
  const dMax = Math.min(constraints.lotDepth.max, constraints.lotDepth.hard ? constraints.lotDepth.max : preset.lotDepth.max);

  let width = rng.range(Math.min(wMin, wMax), Math.max(wMin, wMax));
  const depth = rng.range(Math.min(dMin, dMax), Math.max(dMin, dMax));

  // Garage reservation. The garage generator can produce two layouts:
  //
  //  - frontFacing (preferred): the garage hangs off the body's left
  //    or right side and its door faces the street. Needs ~6 m lateral
  //    room (double-car width) and ~5 m front-to-back room for the
  //    driveway run.
  //  - sideFacing (legacy): the garage projects fully outward from a
  //    side facade by GARAGE_LENGTH (~6 m).
  //
  // To make EITHER layout reliably feasible we widen the lot AND deepen
  // the front setback when a garage is requested. We also force a
  // generous right-side setback so the footprint generator keeps the
  // body on the left, leaving the right side free for the garage wing.
  // (The footprint generator picks one corner of the buildable area; a
  // right-biased setback nudges it left.)
  const GARAGE_LATERAL = 7.0;
  const GARAGE_FRONT = 5.0;
  if (constraints.garage.value && !constraints.lotWidth.hard) {
    width = Math.max(width, Math.min(wMax, width + GARAGE_LATERAL));
  }

  let setbackFront = rng.range(1.5, 3.0);
  const setbackBack = rng.range(1.5, 3.5);
  const setbackLeft = rng.range(1.0, 2.0);
  let setbackRight = rng.range(1.0, 2.0);

  if (constraints.garage.value) {
    setbackRight = Math.max(setbackRight, GARAGE_LATERAL);
    // Ensure room for a driveway in the front yard. We avoid clamping
    // setbackFront so high it pushes the body to the back; just ensure
    // a usable minimum.
    setbackFront = Math.max(setbackFront, GARAGE_FRONT);
  }

  return {
    width,
    depth,
    baseThickness: 0.15,
    setbackFront,
    setbackBack,
    setbackLeft,
    setbackRight,
  };
}
